/**
 * EntityChip - Shared component for rendering clickable entity links.
 *
 * Displays a chip with an icon and label that links to the entity's detail page.
 * Supports items, boards, workspaces, users, agents, and chats.
 * Icon color matches the target entity's module theme color.
 */
import React from 'react';
import {
  ClipboardList,
  LayoutGrid,
  Folder,
  File,
  User,
  Bot,
  MessageSquare,
  Building2,
  Contact,
  Handshake,
  Briefcase,
  UserSearch,
  FileText,
  ClipboardCheck,
  GitFork,
  FolderTree,
  Package,
  X,
  Pencil,
  Eye,
  Plus,
  Trash2,
} from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { cn } from '../../../lib/utils';
import { Badge } from '../../../components/ui/badge';
import type { ResolvedEntity } from '../../types';
import { getEntityByTable, getEntityByTableSuffix } from '../../config/modules.config';
import { getEntityUrl as getEntityUrlShared } from '../../utils/entityUrls';

export interface EntityChipProps {
  /** Resolved entity data from the API */
  entity: ResolvedEntity;
  /** Chip size */
  size?: 'sm' | 'default';
  /** Chip variant */
  variant?: 'outline' | 'default';
  /**
   * Character budget for the label, applied before the CSS-level containment
   * below. Keeps a chip from claiming the whole row in a container that has no
   * width of its own (an unbounded flex row on a detail page). Ignored in
   * `overflow="wrap"` mode, where the full label is shown and wrapped.
   */
  maxLabelLength?: number;
  /**
   * Cap the chip tighter than its container. Accepts a CSS length (e.g.
   * `'12rem'`) or a number (interpreted as px). A chip is ALWAYS capped at
   * 100% of its container — pass this only to make it narrower still.
   */
  maxWidth?: number | string;
  /**
   * How a label too long for the chip's width is contained:
   * - `'truncate'` (default) — one line, ellipsized. Keeps table rows a single
   *   line high, so this is the right mode in a list-page cell.
   * - `'wrap'` — the full label wraps across lines, clamped to `maxLines`. For
   *   roomier surfaces (detail-page panels, drawers) where the whole name matters.
   */
  overflow?: 'truncate' | 'wrap';
  /**
   * In `overflow="wrap"` mode, the wrapped label is clamped to this many lines
   * and then ellipsized. Defaults to 2 rows. Ignored when truncating.
   */
  maxLines?: 1 | 2 | 3 | 4;
  /** For items: link to full page instead of board drawer (default: false) */
  linkToFullPage?: boolean;
  /** Callback when delete icon is clicked. If provided, shows delete icon. */
  onDelete?: (entity: ResolvedEntity) => void;
  /** Open link in a new browser tab */
  openInNewTab?: boolean;
  /** Additional className */
  className?: string;
}

/**
 * Normalize entity type aliases to canonical table names.
 * Email integration stores some refs with legacy 'crm_' prefixes.
 */
function normalizeEntityType(entityType: string): string {
  switch (entityType) {
    case 'crm_contacts':
      return 'contacts';
    case 'crm_companies':
      return 'companies';
    default:
      return entityType;
  }
}

/** Get the appropriate icon for an entity type */
function getEntityIcon(entityType: string): React.ReactElement {
  const iconClass = 'h-3 w-3';
  switch (entityType) {
    case 'tasks':
      return <ClipboardList className={iconClass} />;
    case 'boards':
      return <LayoutGrid className={iconClass} />;
    case 'workspaces':
      return <Folder className={iconClass} />;
    case 'folders':
      return <Folder className={iconClass} />;
    case 'files':
      return <File className={iconClass} />;
    case 'users':
      return <User className={iconClass} />;
    case 'agents':
      return <Bot className={iconClass} />;
    case 'chats':
      return <MessageSquare className={iconClass} />;
    case 'contacts':
      return <Contact className={iconClass} />;
    case 'pipeline_deals':
      return <Handshake className={iconClass} />;
    case 'companies':
      return <Building2 className={iconClass} />;
    case 'recruiting_job_postings':
      return <Briefcase className={iconClass} />;
    case 'recruiting_candidates':
      return <UserSearch className={iconClass} />;
    case 'fbpm_forms':
      return <FileText className={iconClass} />;
    case 'fbpm_scorecards':
      return <ClipboardCheck className={iconClass} />;
    case 'fbpm_business_processes':
      return <GitFork className={iconClass} />;
    case 'fbpm_process_instances':
      return <GitFork className={iconClass} />;
    case 'ctr_contracts':
      return <FileText className={iconClass} />;
    case 'proc_purchase_orders':
      return <FileText className={iconClass} />;
    case 'accounting_bills':
      return <FileText className={iconClass} />;
    case 'contacts_organizations':
      return <Building2 className={iconClass} />;
    case 'tenants':
      return <Building2 className={iconClass} />;
    case 'product_categories':
      return <FolderTree className={iconClass} />;
    case 'products':
      return <Package className={iconClass} />;
    default:
      return <ClipboardList className={iconClass} />;
  }
}

/** Get the module color for an entity type (table name) */
function getModuleColorForEntity(entityType: string): string | undefined {
  const entityInfo = getEntityByTable(entityType) ?? getEntityByTableSuffix(entityType);
  return entityInfo?.module.color;
}

/** Get the URL for an entity based on its type and metadata */
function getEntityUrl(entity: ResolvedEntity, linkToFullPage = false): string | null {
  return getEntityUrlShared({
    entityType: entity.entity_type,
    entityId: entity.entity_id,
    boardId: entity.board_id,
    linkToFullPage,
  });
}

/** Get an action indicator icon for CRUD operations */
function getActionIcon(action?: 'create' | 'read' | 'update' | 'delete'): React.ReactElement | null {
  if (!action) return null;
  const cls = 'h-3 w-3 flex-shrink-0';
  switch (action) {
    case 'create':
      return <Plus className={cn(cls, 'text-success')} />;
    case 'read':
      return <Eye className={cn(cls, 'text-muted-foreground')} />;
    case 'update':
      return <Pencil className={cn(cls, 'text-warning')} />;
    case 'delete':
      return <Trash2 className={cn(cls, 'text-destructive')} />;
    default:
      return null;
  }
}

/**
 * Map a wrapped chip's line cap to its static Tailwind `line-clamp-*` utility.
 * Static entries (not `line-clamp-${n}`) so Tailwind's JIT emits them. Each
 * clamps the label to N rows and appends an ellipsis.
 */
const LINE_CLAMP_CLASS: Record<1 | 2 | 3 | 4, string> = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
};

/** Truncate text with ellipsis if it exceeds maxLength */
function truncateLabel(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength - 1)}...`;
}

/**
 * Renders a chip that links to an entity's detail page.
 * Icon color uses the target module's theme color.
 *
 * @example
 * ```tsx
 * <EntityChip
 *   entity={{ entity_type: 'items', entity_id: 'abc', title: 'Fix login bug', board_id: 'xyz' }}
 *   size="sm"
 * />
 * ```
 */
export function EntityChip({
  entity,
  size = 'sm',
  variant = 'outline',
  maxLabelLength = 30,
  maxWidth,
  overflow = 'truncate',
  maxLines = 2,
  linkToFullPage = false,
  onDelete,
  openInNewTab,
  className,
}: EntityChipProps) {
  const normalizedType = normalizeEntityType(entity.entity_type);
  const normalizedEntity = normalizedType !== entity.entity_type
    ? { ...entity, entity_type: normalizedType }
    : entity;
  const url = getEntityUrl(normalizedEntity, linkToFullPage);
  const label = entity.title || entity.entity_ref;
  // Wrap mode shows the whole label across lines; truncate mode keeps the chip
  // one line high and spends its character budget first.
  const wrap = overflow === 'wrap';
  const truncatedLabel = wrap ? label : truncateLabel(label, maxLabelLength);
  const icon = getEntityIcon(normalizedType);
  const moduleColor = getModuleColorForEntity(normalizedType);
  const maxWidthValue = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;

  const handleDelete = onDelete
    ? (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        onDelete(entity);
      }
    : undefined;

  const actionIcon = getActionIcon(entity.action);

  const content = (
    <>
      <span className={cn('inline-flex shrink-0 items-center gap-0.5', wrap && 'mt-0.5')}>
        <span style={{ color: moduleColor }}>{icon}</span>
        {actionIcon}
      </span>
      <span
        dir="auto"
        className={cn(
          'min-w-0',
          wrap
            ? cn('whitespace-normal break-words', LINE_CLAMP_CLASS[maxLines])
            : 'truncate'
        )}
      >
        {truncatedLabel}
      </span>
      {onDelete && (
        <button
          onClick={handleDelete}
          className="ms-1 shrink-0 rounded-full p-0.5 hover:bg-muted"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </>
  );

  const chipClassName = cn(
    // A chip is always contained: it can never grow past its container, and it
    // may shrink below its content width so the label's truncation kicks in.
    'inline-flex items-center gap-1.5 font-normal max-w-full min-w-0',
    size === 'sm' && 'text-xs px-2 py-0.5',
    // Wrapping chips run their label across lines: top-align the icon, allow
    // multi-line height, and square the corners (a full pill looks odd on
    // multiple lines).
    wrap && 'items-start whitespace-normal rounded-md',
    url && 'cursor-pointer hover:bg-muted/80',
    className
  );

  const style: React.CSSProperties | undefined =
    moduleColor || maxWidthValue
      ? { ...(moduleColor ? { borderColor: moduleColor } : {}), ...(maxWidthValue ? { maxWidth: maxWidthValue } : {}) }
      : undefined;

  // The label can be shortened by its character budget OR clipped by the chip's
  // width, and the second is invisible to JS — so always carry the full name in
  // a title tooltip.
  const title = label;

  // If we can't generate a URL, render a non-clickable chip
  if (!url) {
    return (
      <Badge variant={variant} className={chipClassName} style={style} title={title}>
        {content}
      </Badge>
    );
  }

  return (
    // The anchor has to be contained too, or the badge's `max-w-full` resolves
    // against an inline box that has already outgrown the cell.
    // eslint-disable-next-line i18next/no-literal-string -- HTML anchor attribute tokens, not user copy
    <RouterLink to={url} className="no-underline inline-flex min-w-0 max-w-full align-middle" {...(openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
      <Badge variant={variant} className={chipClassName} style={style} title={title}>
        {content}
      </Badge>
    </RouterLink>
  );
}
